import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, TrendingUp, User, Gauge, ArrowUpRight, Sparkles, CheckCircle2, Circle, Activity,
} from "lucide-react";
import { api } from "../api";
import { GlassCard, ScoreRing, ProgressBar, Spinner, Disclaimer } from "../components/ui";
import { DISCLAIMER } from "../constants";

const SCORE_CARDS = [
  { key: "foundation", label: "Business Foundation", icon: Building2, to: "/credit/app/business-builder" },
  { key: "business_credit", label: "Business Credit", icon: TrendingUp, to: "/credit/app/business-credit" },
  { key: "personal_credit", label: "Personal Credit", icon: User, to: "/credit/app/personal-credit" },
  { key: "funding", label: "Funding Readiness", icon: Gauge, to: "/credit/app/funding" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === null) return <Spinner />;
  if (data === false) return <div className="text-slate-400">Could not load dashboard.</div>;

  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs tracking-[0.2em] uppercase text-slate-500">Overview</span>
        <h1 className="cc-heading text-3xl sm:text-4xl font-600 tracking-tight text-white mt-1">Dashboard</h1>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {SCORE_CARDS.map((c, i) => (
          <motion.div key={c.key}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.5 }}>
            <Link to={c.to}>
              <GlassCard className="p-5 hover:ring-1 hover:ring-blue-500/40 transition-shadow" data-testid={`score-card-${c.key}`}>
                <div className="flex items-start justify-between">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg cc-surface-2 text-blue-300">
                    <c.icon size={17} />
                  </div>
                  <ArrowUpRight size={15} className="text-slate-600" />
                </div>
                <div className="cc-heading text-4xl font-700 text-white mt-4">{data.scores[c.key]}</div>
                <div className="text-xs text-slate-500 mt-0.5">out of 100</div>
                <div className="mt-3"><ProgressBar value={data.scores[c.key]} /></div>
                <div className="text-sm text-slate-300 mt-2.5">{c.label}</div>
              </GlassCard>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Funding ring */}
        <GlassCard className="p-6 flex flex-col items-center justify-center text-center">
          <ScoreRing value={data.scores.funding} label="Readiness" size={150} />
          <h3 className="cc-heading text-lg font-500 text-white mt-4">Funding Readiness</h3>
          <p className="text-slate-400 text-sm mt-1">Your educational readiness score</p>
          <Link to="/credit/app/funding" className="mt-4 text-sm text-blue-300 hover:text-blue-200 inline-flex items-center gap-1">
            View breakdown <ArrowUpRight size={14} />
          </Link>
        </GlassCard>

        {/* Today's tasks */}
        <GlassCard className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="cc-heading text-lg font-500 text-white">Today's Tasks</h3>
            <Link to="/credit/app/tasks" className="text-sm text-blue-300 hover:text-blue-200">View all</Link>
          </div>
          <div className="space-y-2.5">
            {data.tasks.length === 0 && <p className="text-slate-500 text-sm">You're all caught up. 🎯</p>}
            {data.tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-lg cc-surface px-4 py-3 border border-white/[0.04]">
                <Circle size={16} className="text-slate-600 shrink-0" />
                <span className="text-sm text-slate-200">{t.title}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* AI recommendations */}
        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={17} className="text-purple-300" />
            <h3 className="cc-heading text-lg font-500 text-white">AI Recommendations</h3>
          </div>
          <div className="space-y-2.5">
            {data.recommendations.map((r, i) => (
              <div key={`${r.slice(0, 40)}-${i}`} className="flex items-start gap-2.5 text-sm text-slate-300">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-blue-400" />
                {r}
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Recent activity */}
        <GlassCard className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={17} className="text-blue-300" />
            <h3 className="cc-heading text-lg font-500 text-white">Recent Activity</h3>
          </div>
          <div className="space-y-3">
            {data.activity.length === 0 && <p className="text-slate-500 text-sm">No activity yet.</p>}
            {data.activity.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{a.text}</span>
                <span className="text-slate-600 text-xs">{new Date(a.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <Disclaimer text={DISCLAIMER} />
    </div>
  );
}
